<template>
  <loading-wrapper :loading="isLoading">
    <validated-form
      data-test-id="survey-form-wrapper"
      name="mobile-tip"
      @valid-submit="submitSurveyForm"
    >
      <div class="padding-x flex-column gap">
        <!-- Tip Selection -->
        <tip-selection
          v-model="tipAmount"
          data-test-id="tip-selection"
          custom-radio-style="box-grid"
          :label="content.MobileTippingSubmit.metadata.tippingTitle"
          :total="0"
          show-as-optional
          show-button-label
        />

        <!-- Survey Section -->
        <div
          v-if="hasActiveSurvey"
          data-test-id="survey-wrapper"
        >
          <template v-for="question in survey.questions">
            <star-rating-input
              v-if="question.questionType === 'StarRating'"
              :key="question.id"
              v-model="question.answer"
              :label="question.label"
              :name="`surveyQuestion${question.id}`"
              :validations="{ required: question.required }"
              :show-as-optional="!question.required"
            />

            <radio-input
              v-else-if="question.questionType === 'RadioList'"
              :key="question.id"
              v-model="question.answer"
              :label="question.label"
              :name="`surveyQuestion${question.id}`"
              :options="question.options.map(option => ({ ...option, display: option.value }))"
              :validations="{ required: question.required }"
              :show-as-optional="!question.required"
            />

            <checklist-input
              v-else-if="question.questionType === 'Checklist' || question.questionType === 'MultipleSelect'"
              :key="question.id"
              v-model="question.answer"
              :label="question.label"
              :name="`surveyQuestion${question.id}`"
              :options="question.options.map(option => ({ ...option, display: option.value }))"
              :validations="{ required: question.required }"
              :show-as-optional="!question.required"
            />

            <text-input
              v-else-if="question.questionType === 'Textarea'"
              :key="question.id"
              v-model="question.answer"
              type="textarea"
              :label="question.label"
              :name="`surveyQuestion${question.id}`"
              :validations="{ required: question.required }"
              :show-as-optional="!question.required"
            />

            <text-input
              v-else-if="question.questionType === 'Input'"
              :key="question.id"
              v-model="question.answer"
              type="text"
              :label="question.label"
              :name="`surveyQuestion${question.id}`"
              :validations="{ required: question.required }"
              :show-as-optional="!question.required"
            />
          </template>
        </div>

        <!-- Payment Methods -->
        <payment-methods
          v-if="shouldDisplayPaymentButtons"
          data-test-id="payment-methods"
          :primary-cta-text="paymentButtonCta"
          :disabled="shouldDisablePaymentButtons"
          pay-cta-text="Pay Now"
          payment-modal-title="Payment Details"
          :total="0"
          :hidden-elements="{
            giftCards: true,
            signIn: true,
            tendr: true,
            total: true
          }"
          @checkout="submitPaymentForm"
        >
          <template #payment-modal-top>
            <div class="section section--half-y-padding center">
              <p class="title title--1 primary no-spacing">
                {{ checkoutTipAmountContent }}
              </p>
              <p class="bold">Your Tip Amount</p>
            </div>
          </template>
        </payment-methods>

        <!-- Continue Button -->
        <button
          v-else
          data-test-id="continue-button"
          :disabled="hasAllUnansweredOptionalQuestions"
          :class="[
            'button button--large button--primary',
            { 'button--loading': isSubmitting }
          ]"
          type="submit"
        >
          CONTINUE
        </button>

      </div>
    </validated-form>
  </loading-wrapper>
</template>

<script>
  import { mapGetters } from 'vuex';

  import pay from '@/services/pay';
  import errorHandlers from '@/services/error-handlers';
  import { getSurvey, submitSurvey } from '@/services/api/surveys';

  import dollars from '@/filters/dollars';

  import checklistInput from './survey-inputs/checklist-input.vue';
  import starRatingInput from './survey-inputs/star-rating.vue';


  export default {
    name: 'MobileTippingForm',

    components: { checklistInput, starRatingInput },

    props: {
      storeId: {
        type: Number,
        required: true
      },

      departmentPublicId: {
        type: String,
        default: null
      },

      employeePublicId: {
        type: String,
        default: null
      }
    },

    data: () => ({
      isLoading: false,
      isSubmitting: false,
      survey: null,
      tipAmount: 0
    }),

    computed: {
      ...mapGetters('content', ['content']),
      ...mapGetters('locations', ['selectedLocation']),

      hasActiveSurvey() {
        return this.survey && this.survey.isActive;
      },

      shouldDisplayPaymentButtons() {
        return this.tipAmount || (!this.hasActiveSurvey && !this.tipAmount);
      },

      shouldDisablePaymentButtons() {
        return !this.tipAmount && !this.hasActiveSurvey;
      },

      paymentButtonCta() {
        return `CONTINUE${this.tipAmount ? ` - ${dollars(this.tipAmount)}` : ''}`;
      },

      checkoutTipAmountContent() {
        return dollars(this.tipAmount);
      },

      hasAllUnansweredOptionalQuestions() {
        return this.survey?.questions?.every(question => !question.answer && !question.required);
      }
    },

    created() {
      this.onCreated();
    },

    methods: {
      dollars,

      async onCreated() {
        try {
          this.isLoading = true;

          this.$store.dispatch('session/updateLocationId', this.storeId);

          await this.$store.dispatch('locations/getLocation');
          await this.$store.dispatch('orders/updateOrder', { orderType: 'tip-only' });

          this.survey = await getSurvey(this.$route.query.source, 'MobileTippingSurvey');
        }
        catch (error) {
          errorHandlers.generic(error);
        }
        finally {
          this.isLoading = false;
        }
      },

      redirectToReceiptPage(receiptToken) {
        const routerDestination = {
          name: 'tipReceiptPage'
        };

        if (receiptToken) {
          Object.assign(routerDestination, { params: { receiptToken } });
        }

        this.$router.push(routerDestination);
      },

      async submitSurveyForm() {
        try {
          this.isSubmitting = true;

          await submitSurvey(this.survey);

          this.redirectToReceiptPage();
        }
        catch (error) {
          errorHandlers.generic(error);
        }
        finally {
          this.isSubmitting = false;
        }
      },

      formatPayment(contactInfo, paymentMethod, order) {
        return {
          contactInfo,
          paymentMethod,
          order,
          total: 0,
          tip: this.tipAmount,
          ...this.departmentPublicId && { tipDepartmentPublicId: this.departmentPublicId },
          ...this.employeePublicId && { tipEmployeePublicId: this.employeePublicId },
          returnFirstPayment: true,
          completePayment: false /* this is only needed for the P@T experience  *
            * where there can potentially be multiple users paying via websockets */
        };
      },

      async submitPaymentForm({ contactInfo, paymentMethod }) {
        try {
          this.isSubmitting = true;

          const order = await this.$store.dispatch('orders/startTipOnlyOrder');

          const paymentInfo = this.formatPayment(contactInfo, paymentMethod, order);

          const { receiptToken } = await pay.addPayment(paymentInfo);

          if (this.survey?.isActive) await submitSurvey(this.survey, order.orderId);

          this.redirectToReceiptPage(receiptToken);
        }
        catch (error) {
          errorHandlers.generic(error);
        }
        finally {
          this.isSubmitting = false;
        }
      }

    }
  };
</script>
